import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/Logo";

const tabs = [
  { label: "Home", to: "/", end: true },
  { label: "About", to: "/about" },
  { label: "Work", to: "/work" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <div className="sm:hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close menu" : "Open menu"}
        data-testid="mobile-menu-toggle"
        className="flex h-10 w-10 items-center justify-center rounded-full text-jade transition-colors duration-300 hover:bg-jade/5"
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-x-0 top-16 border-b border-jade/10 bg-paper px-6 pb-6 pt-2 shadow-ambient"
            data-testid="mobile-menu"
          >
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                data-testid={`mobile-nav-${tab.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `block border-b border-jade/10 py-3 font-display text-xl ${isActive ? "text-jade" : "text-ink"}`
                }
              >
                {tab.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta-button"
              className="mt-5 block rounded-full bg-jade py-3 text-center text-sm font-medium text-paper"
            >
              Start a project
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </div>
  );
};

const Nav = () => (
  <motion.header
    initial={{ y: -64, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    className="fixed inset-x-0 top-0 z-50 border-b border-jade/10 bg-paper/70 backdrop-blur-md"
    data-testid="site-nav"
  >
    <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
      <Link
        to="/"
        data-testid="nav-logo-link"
        className="text-jade transition-opacity duration-300 hover:opacity-70"
      >
        <Wordmark />
      </Link>
      <nav className="hidden items-center gap-6 sm:flex lg:gap-8">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            data-testid={`nav-${tab.label.toLowerCase()}-link`}
            className={({ isActive }) =>
              `text-sm font-medium transition-colors duration-300 ${
                isActive
                  ? "text-jade underline-offset-8 [text-decoration:underline] decoration-jade/50"
                  : "text-ink2 hover:text-jade"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
        <Link
          to="/contact"
          data-testid="nav-cta-button"
          className="rounded-full bg-jade px-5 py-2 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink"
        >
          Start a project
        </Link>
      </nav>
      <MobileMenu />
    </div>
  </motion.header>
);

export default Nav;